import {
  Activity,
  Bot,
  Calendar,
  CreditCard,
  FileJson,
  FileUp,
  LayoutDashboard,
  MessageSquareText,
  Phone,
  Play,
  Settings,
  Sparkles,
  Users,
  UserCircle,
  Workflow,
} from "lucide-react";
import React from "react";

export interface NavItem {
  // Stable id: the item's sidebar flag is feature_flags["nav.<key>"]
  key: string;
  icon: React.ReactNode;
  label: string;
  href: string;
  requiresPro: boolean;
  isTemplate?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { key: "dashboard", icon: <LayoutDashboard />, label: "Dashboard", href: "/dashboard", requiresPro: false },
  { key: "assistants", icon: <Bot />, label: "Assistants", href: "/assistants", requiresPro: false },
  { key: "personas", icon: <UserCircle />, label: "Personas", href: "/personas", requiresPro: false },
  { key: "simulators", icon: <Play />, label: "Simulators", href: "/simulators", requiresPro: false },
  { key: "prompt_engine", icon: <MessageSquareText />, label: "Prompt Engine", href: "/prompt-engine", requiresPro: false },
  { key: "micro_prompts", icon: <Sparkles />, label: "Micro Prompts", href: "/micro-prompts", requiresPro: false },
  { key: "leads", icon: <Users />, label: "Leads", href: "/leads", requiresPro: false },
  { key: "lead_uploads", icon: <FileUp />, label: "Lead Uploads", href: "/lead-uploads", requiresPro: false },
  { key: "providers", icon: <Settings />, label: "Providers", href: "/providers", requiresPro: false },
  { key: "credits_balance", icon: <CreditCard />, label: "Credits Balance", href: "/credits-balance", requiresPro: false },
  { key: "analytics", icon: <Calendar />, label: "Analytics", href: "/analytics", requiresPro: true },
  { key: "phone_numbers", icon: <Phone />, label: "Phone Numbers", href: "/phone-numbers", requiresPro: true },
  { key: "call_requests", icon: <Phone className="rotate-180" />, label: "Call Requests", href: "/call-requests", requiresPro: false },
  { key: "schema_templates", icon: <FileJson />, label: "Schema Templates", href: "/schema-templates", requiresPro: false },
  { key: "call_logs", icon: <MessageSquareText />, label: "Call Logs", href: "/call-logs", requiresPro: false },
  { key: "latency_analytics", icon: <Activity />, label: "Latency Analytics", href: "/analytics/latency", requiresPro: false },
  { key: "scheduled_calls", icon: <Calendar />, label: "Scheduled Calls", href: "/scheduled-calls", requiresPro: false },
  { key: "callflows", icon: <Workflow />, label: "CallFlows", href: "/callflows", requiresPro: false },
  { key: "slots", icon: <MessageSquareText />, label: "Slots", href: "/slots", requiresPro: false },
  { key: "feature_templates", icon: <FileJson />, label: "Feature Templates", href: "/feature-templates", requiresPro: false },
];

// The sidebar's "Templates" group (an organization's use-case templates under /u/...)
export const TEMPLATES_NAV_KEY = "templates";

export const SIDEBAR_TOGGLES: { key: string; label: string; href: string }[] = [
  ...NAV_ITEMS.map(({ key, label, href }) => ({ key, label, href })),
  { key: TEMPLATES_NAV_KEY, label: "Templates group", href: "/u" },
];

export const navFlag = (key: string) => `nav.${key}`;

/**
 * Whether an organization shows a sidebar item. Set by platform admins on the organization's
 * features page; a missing flag counts as on, so organizations configured before these flags
 * existed keep the whole sidebar.
 */
export const isNavEnabled = (
  flags: Record<string, boolean> | undefined,
  key: string,
) => flags?.[navFlag(key)] !== false;

/** The sidebar item owning a path: the longest href it falls under (/analytics/latency before /analytics). */
export function navKeyForPath(pathname: string): string | null {
  let best: { key: string; href: string } | null = null;
  for (const { key, href } of SIDEBAR_TOGGLES) {
    if (pathname === href || pathname.startsWith(`${href}/`)) {
      if (!best || href.length > best.href.length) best = { key, href };
    }
  }
  return best?.key ?? null;
}
